import {
  PROTOCOL_VERSION,
  WSMessageFromServer,
  type WSMessageFromClient,
} from "@radar-sim/protocol";
import type { Command, CommandResult, WorldSnapshot } from "@radar-sim/schema";
import type {
  ConnectionState,
  RadarStateSource,
  Unsubscribe,
} from "./state-source.js";

/**
 * Minimal WebSocket-like surface — the source itself accepts an instance
 * conforming to this rather than the global `WebSocket` so it can be tested
 * with an in-memory fake. Production callers pass a real `WebSocket`.
 */
export interface WebSocketLike {
  send(data: string): void;
  close(): void;
  addEventListener(
    type: "open" | "message" | "close" | "error",
    listener: (ev: { data?: string }) => void,
  ): void;
  readyState: number;
}

export interface DurableObjectSourceOptions {
  /**
   * Factory that returns a WebSocket-like instance. Called on `connect()` and
   * again for each automatic reconnect. The factory typically embeds the
   * `wsTicket` from `joinSession` in the URL or subprotocol.
   */
  factory: () => WebSocketLike;
  /** Maximum number of automatic reconnect attempts. Default 5. */
  maxReconnectAttempts?: number;
  /** Backoff between reconnect attempts in ms. Default starts at 250 doubling. */
  reconnectBackoffMs?: (attempt: number) => number;
}

const DEFAULT_BACKOFF = (attempt: number): number =>
  Math.min(8000, 250 * 2 ** attempt);

let cmdCounter = 0;
function nextCmdId(): string {
  cmdCounter += 1;
  return `c-${Date.now()}-${cmdCounter}`;
}

/**
 * Cloudflare-Durable-Object-backed radar state source. Connects to a session
 * over WebSocket, validates every inbound frame against
 * `WSMessageFromServer`, exposes the canonical world snapshot stream to the
 * same consumers that drive `LocalSimSource` and `ReplaySource`.
 *
 * Reconnection. Drops trigger automatic re-attempts with exponential
 * backoff up to `maxReconnectAttempts`. The DO replays a hello + the
 * latest snapshot on each successful reconnect, so consumers seamlessly
 * pick up where they left off; per-client display state is held locally
 * and is not affected by the network drop.
 */
export class DurableObjectSource implements RadarStateSource {
  readonly mode = "classroom" as const;

  private connectionState: ConnectionState = "idle";
  private socket: WebSocketLike | undefined;
  private reconnectAttempts = 0;
  private explicitlyClosed = false;
  private _tickRateHz = 10;

  private readonly snapshotHandlers = new Set<(s: WorldSnapshot) => void>();
  private readonly stateHandlers = new Set<(s: ConnectionState) => void>();
  private readonly cursorHandlers = new Set<
    (cursor: {
      bearingDeg: number;
      rangeNm: number;
      visible: boolean;
    }) => void
  >();
  private readonly pendingCommands = new Map<
    string,
    (result: CommandResult) => void
  >();

  private readonly factory: () => WebSocketLike;
  private readonly maxReconnects: number;
  private readonly backoffMs: (attempt: number) => number;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(opts: DurableObjectSourceOptions) {
    this.factory = opts.factory;
    this.maxReconnects = opts.maxReconnectAttempts ?? 5;
    this.backoffMs = opts.reconnectBackoffMs ?? DEFAULT_BACKOFF;
  }

  get state(): ConnectionState {
    return this.connectionState;
  }

  get tickRateHz(): number {
    return this._tickRateHz;
  }

  async connect(): Promise<void> {
    this.explicitlyClosed = false;
    if (
      this.connectionState === "active" ||
      this.connectionState === "connecting"
    ) {
      return;
    }
    this.openSocket();
  }

  async disconnect(): Promise<void> {
    this.explicitlyClosed = true;
    if (this.reconnectTimer !== undefined) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    if (this.socket !== undefined) {
      this.socket.close();
      this.socket = undefined;
    }
    this.transitionState("disconnected");
  }

  subscribe(handler: (snapshot: WorldSnapshot) => void): Unsubscribe {
    this.snapshotHandlers.add(handler);
    return () => {
      this.snapshotHandlers.delete(handler);
    };
  }

  onStateChange(handler: (state: ConnectionState) => void): Unsubscribe {
    this.stateHandlers.add(handler);
    return () => {
      this.stateHandlers.delete(handler);
    };
  }

  onBroadcastCursor(
    handler: (cursor: {
      bearingDeg: number;
      rangeNm: number;
      visible: boolean;
    }) => void,
  ): Unsubscribe {
    this.cursorHandlers.add(handler);
    return () => {
      this.cursorHandlers.delete(handler);
    };
  }

  async sendCommand(command: Command): Promise<CommandResult> {
    if (this.socket === undefined || this.connectionState !== "active") {
      return {
        ok: false,
        code: "invalid_state",
        reason: "source is not connected",
      };
    }
    const cmdId = nextCmdId();
    const envelope: WSMessageFromClient = {
      protocolVersion: PROTOCOL_VERSION,
      kind: "command",
      cmdId,
      command,
    };
    this.socket.send(JSON.stringify(envelope));
    return new Promise<CommandResult>((resolve) => {
      this.pendingCommands.set(cmdId, resolve);
    });
  }

  /** Instructor convenience: broadcast a cursor to every subscriber. */
  broadcastCursor(cursor: {
    bearingDeg: number;
    rangeNm: number;
    visible: boolean;
  }): void {
    if (this.socket === undefined || this.connectionState !== "active") return;
    const envelope: WSMessageFromClient = {
      protocolVersion: PROTOCOL_VERSION,
      kind: "setBroadcastCursor",
      ...cursor,
    };
    this.socket.send(JSON.stringify(envelope));
  }

  private openSocket(): void {
    this.transitionState("connecting");
    const socket = this.factory();
    this.socket = socket;
    socket.addEventListener("open", () => {
      this.reconnectAttempts = 0;
    });
    socket.addEventListener("message", (ev) => {
      this.handleMessage(typeof ev.data === "string" ? ev.data : "");
    });
    socket.addEventListener("close", () => {
      this.handleClose();
    });
    socket.addEventListener("error", () => {
      // Errors propagate to close in browser WebSocket semantics; just log
      // and let the close handler run.
    });
  }

  private handleMessage(raw: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    const result = WSMessageFromServer.safeParse(parsed);
    if (!result.success) return;
    const msg = result.data;
    switch (msg.kind) {
      case "hello":
        this._tickRateHz = msg.tickRateHz;
        this.transitionState("active");
        return;
      case "snapshot":
        for (const handler of this.snapshotHandlers) handler(msg.snapshot);
        return;
      case "commandResult": {
        const resolver = this.pendingCommands.get(msg.cmdId);
        if (resolver !== undefined) {
          this.pendingCommands.delete(msg.cmdId);
          if (msg.ok) {
            resolver({ ok: true });
          } else {
            resolver({
              ok: false,
              code: msg.code ?? "rejected_by_source",
              reason: msg.reason ?? "command rejected",
            });
          }
        }
        return;
      }
      case "broadcastCursor":
        for (const handler of this.cursorHandlers) {
          handler({
            bearingDeg: msg.bearingDeg,
            rangeNm: msg.rangeNm,
            visible: msg.visible,
          });
        }
        return;
      case "error":
        if (msg.fatal) {
          this.explicitlyClosed = true;
          this.transitionState("error");
        }
        return;
    }
  }

  private handleClose(): void {
    this.socket = undefined;
    if (this.explicitlyClosed) {
      this.transitionState("disconnected");
      return;
    }
    if (this.reconnectAttempts >= this.maxReconnects) {
      this.transitionState("error");
      return;
    }
    const delay = this.backoffMs(this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.transitionState("connecting");
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (!this.explicitlyClosed) this.openSocket();
    }, delay);
  }

  private transitionState(next: ConnectionState): void {
    if (this.connectionState === next) return;
    this.connectionState = next;
    for (const handler of this.stateHandlers) handler(next);
  }
}
